import type * as api from '@gitbook/api-client';
import { Logger } from '@gitbook/util-debug';
import {
    createCache,
    createFastCacheKeyWithCollisionRisk,
    type SuspenseCache,
} from '@gitbook/util-suspense-cache';

// Prevent circular dependency
import { resolveDynamicBinding } from './elements/bindings';

const logger = Logger('contentkit:remote');

export interface ContentKitRemoteInput {
    integrationId: string;
    componentId: string;
    props: object;
    state?: object;
    action?: api.ContentKitAction;
    context: api.ContentKitContext;
}

export type ContentKitRemoteOutput = api.ContentKitRenderOutput;

export type ContentKitRemote = SuspenseCache<ContentKitRemoteInput, ContentKitRemoteOutput>;

/**
 * Create a cache to fetch remote components.
 */
export function createContentKitRemote(
    getValue: (
        input: ContentKitRemoteInput
    ) => ContentKitRemoteOutput | Promise<ContentKitRemoteOutput>
): ContentKitRemote {
    return createCache<ContentKitRemoteInput, ContentKitRemoteOutput>({
        getKey: (input) =>
            `${input.integrationId}-${input.componentId}-${createFastCacheKeyWithCollisionRisk(
                input
            )}`,
        getValue: async (input) => {
            logger.info('fetch', input);
            const value = await getValue(input);
            logger.info('got response', value);

            return value;
        },
    });
}

/**
 * Get the URL string for a ContentKit URL. This will resolve dynamic bindings in the query
 * using the local state.
 */
export function getContentKitURLString(
    input: api.ContentKitURL | string,
    localState: object
): string {
    if (typeof input === 'string') {
        return input;
    }

    const query: Record<string, string | undefined> = {};
    Object.entries(input.query || {}).forEach(([key, value]) => {
        query[key] = resolveDynamicBinding(localState, value);
    });

    const url = new URL(`https://${input.host}${input.pathname}`);
    Object.entries(query).forEach(([key, value]) => {
        if (value && typeof value === 'string') {
            url.searchParams.set(key, value);
        }
    });

    return url.toString();
}

/**
 * Cache for fetching external responses from ContentKit elements
 */
export const fetchCache = createCache<string, { ok: boolean; error?: string; data?: any }>({
    getKey: (input) => input,
    getValue: async (input) => {
        const response = await fetch(input);

        if (!response.ok) {
            let message = response.statusText;
            try {
                message = await response.text();
            } catch (_err) {
                // Ignore
            }
            return { ok: false, error: message };
        }

        return { ok: true, data: await response.json() };
    },
});
